import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useHrefWithParams } from '@/shared/hooks/use-navigate-with-params';
import { useKeyboardScreenStyle } from '@/shared/hooks/use-visual-viewport';
import quizBg from '../../assets/quiz-bg.webp';
import mailIcon from '../../assets/email-mail-icon.svg';
import privacyShield from '../../assets/privacy-shield.svg';
import type { QuizStep } from '../../types';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EmailStepProps {
  step: QuizStep;
  value: string;
  onChange: (value: string) => void;
  onAdvance: () => void;
}

export function EmailStep({ step, value, onChange, onAdvance }: EmailStepProps) {
  const href = useHrefWithParams();
  const keyboardStyle = useKeyboardScreenStyle();
  const [touched, setTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const isValid = EMAIL_RE.test(value.trim());
  const showError = (touched || submitted) && value.length > 0 && !isValid;

  const handleSubmit = () => {
    setSubmitted(true);
    if (!isValid) return;
    onAdvance();
  };

  return (
    <div
      className="relative flex min-h-[100dvh] flex-col overflow-hidden bg-mf-neutral-50 font-body"
      style={keyboardStyle}
    >
      <img
        src={quizBg}
        alt=""
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-1/2 h-auto w-[140%] max-w-none -translate-x-1/2 select-none opacity-90"
      />

      <div className="relative z-10 flex flex-1 flex-col gap-6 px-4 py-6">
        <div className="w-full text-center">
          <h1 className="font-display text-xl font-bold leading-6 text-mf-neutral-900">
            Enter your email address to start tracking{' '}
            <span className="text-mf-green">unclaimed money</span> instantly
          </h1>
          {step.subtitle && (
            <p className="mt-2 text-base leading-5 text-mf-neutral-600">{step.subtitle}</p>
          )}
        </div>

        <div className="flex w-full flex-col gap-1">
          <label htmlFor="quiz-email" className="text-xs leading-4 text-mf-neutral-600">
            Email
          </label>
          <div
            className={cn(
              'flex h-[52px] items-center gap-2 rounded-xl bg-white px-4 shadow-[0_0_16px_rgba(26,22,19,0.03)]',
              showError && 'border-[1.5px] border-[#d6493a]',
            )}
          >
            <img src={mailIcon} alt="" className="size-6 shrink-0" />
            <input
              id="quiz-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onBlur={() => setTouched(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder={step.placeholder || 'Enter your email'}
              className="h-full w-full bg-transparent text-base leading-5 text-mf-neutral-900 outline-none placeholder:text-[#c0b7ae]"
              autoFocus
            />
          </div>
          {showError && (
            <p className="text-xs leading-4 text-[#e53e3e]">Please enter a valid email</p>
          )}
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid}
          className={cn(
            'flex h-[52px] w-full items-center justify-center rounded-full px-5 text-base font-bold text-white transition-opacity',
            isValid
              ? 'bg-mf-neutral-900 hover:opacity-90'
              : 'cursor-not-allowed bg-mf-neutral-900/20',
          )}
        >
          {step.ctaLabel || 'Get started'}
        </button>
      </div>

      <div className="relative z-10 flex items-start gap-3 px-4 py-4">
        <div className="flex h-14 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-[0_0_16px_rgba(26,22,19,0.03)]">
          <img src={privacyShield} alt="" className="h-6 w-5" />
        </div>
        <p className="flex-1 py-1 text-xs leading-4 text-mf-neutral-400">
          We respect your privacy and are commited to protecting your personal data. By continuing,
          you agree to our{' '}
          <a href={href('/terms')} className="text-mf-green">
            Terms &amp; Conditions
          </a>{' '}
          and{' '}
          <a href={href('/privacy')} className="text-mf-green">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
